/**
 * RepeatOffendersTable — replaces the old static table inside Reports.js.
 *
 * Adds (Feb 22, 2026 user ask):
 *   • Per-column filters (text for Staff/Geofence/Department, min/max
 *     numeric ranges for Times Late / % / Total Late Min / Worst).
 *   • Per-column sort (click header to cycle asc → desc → none).
 *   • Result count + "Clear filters" button.
 *   • Totals row recomputes from the FILTERED rows so the subtotal
 *     reflects what the admin is currently looking at.
 *
 * All filters are URL-stateless (kept in component state). The data shape
 * comes from Reports.js — each row has:
 *   { sid, name, geofence, department, total, total_late_min, worst_min }
 */
import React, { useState, useMemo } from 'react';
import { Search, ArrowUp, ArrowDown, ArrowUpDown, X } from 'lucide-react';

const NUM_COLS = ['total', 'pct', 'total_late_min', 'worst_min'];

export default function RepeatOffendersTable({ offenders, threshold, grandTotal }) {
  const [filters, setFilters] = useState({
    name: '',
    geofence: '',
    department: '',
    total_min: '', total_max: '',
    pct_min: '', pct_max: '',
    total_late_min_min: '', total_late_min_max: '',
    worst_min_min: '', worst_min_max: '',
  });
  const [sortBy, setSortBy] = useState({ key: 'total', dir: 'desc' });

  // Decorate rows with `pct` so the filter can compare on the same column
  // that's rendered (otherwise % filter would silently mismatch the cell).
  const decorated = useMemo(
    () => offenders.map(o => ({
      ...o,
      pct: grandTotal > 0 ? (o.total / grandTotal) * 100 : 0,
    })),
    [offenders, grandTotal]
  );

  const filtered = useMemo(() => {
    const ci = (s) => (s || '').toLowerCase();
    const text = {
      name: ci(filters.name),
      geofence: ci(filters.geofence),
      department: ci(filters.department),
    };
    const inRange = (val, min, max) => {
      if (min !== '' && Number(val) < Number(min)) return false;
      if (max !== '' && Number(val) > Number(max)) return false;
      return true;
    };
    return decorated.filter(r =>
      ci(`${r.name} ${r.sid}`).includes(text.name) &&
      ci(r.geofence).includes(text.geofence) &&
      ci(r.department).includes(text.department) &&
      inRange(r.total, filters.total_min, filters.total_max) &&
      inRange(r.pct, filters.pct_min, filters.pct_max) &&
      inRange(r.total_late_min, filters.total_late_min_min, filters.total_late_min_max) &&
      inRange(r.worst_min, filters.worst_min_min, filters.worst_min_max)
    );
  }, [decorated, filters]);

  const sorted = useMemo(() => {
    if (!sortBy.key) return filtered;
    const sign = sortBy.dir === 'asc' ? 1 : -1;
    const isNum = NUM_COLS.includes(sortBy.key);
    return [...filtered].sort((a, b) => {
      const av = a[sortBy.key];
      const bv = b[sortBy.key];
      if (isNum) return (Number(av || 0) - Number(bv || 0)) * sign;
      return String(av || '').localeCompare(String(bv || '')) * sign;
    });
  }, [filtered, sortBy]);

  const handleSort = (key) => {
    setSortBy(prev => {
      if (prev.key !== key) return { key, dir: 'desc' };
      if (prev.dir === 'desc') return { key, dir: 'asc' };
      return { key: null, dir: null };  // 3rd click clears
    });
  };

  const clearFilters = () => setFilters({
    name: '', geofence: '', department: '',
    total_min: '', total_max: '',
    pct_min: '', pct_max: '',
    total_late_min_min: '', total_late_min_max: '',
    worst_min_min: '', worst_min_max: '',
  });

  const hasAnyFilter = Object.values(filters).some(v => v !== '');

  // Subtotal — uses the filtered+sorted rows, NOT the original.
  const subTotalTimes = sorted.reduce((a, s) => a + (s.total || 0), 0);
  const subTotalMin   = sorted.reduce((a, s) => a + (s.total_late_min || 0), 0);
  const subTotalPct   = grandTotal > 0 ? (subTotalTimes / grandTotal) * 100 : 0;

  return (
    <div className="md:col-span-2 bg-white rounded-xl border-2 border-red-200 shadow-sm overflow-hidden" data-testid="repeat-offenders-card">
      <div className="px-4 py-2 border-b bg-red-50 text-sm font-semibold text-red-900 flex items-center justify-between gap-2 flex-wrap">
        <span>🚩 Repeat Offenders — {threshold}+ late check-ins in range</span>
        <div className="flex items-center gap-2 text-xs font-normal">
          <span className="text-red-700">
            Showing <b>{sorted.length}</b> of <b>{offenders.length}</b>
          </span>
          {hasAnyFilter && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1 px-2 py-1 bg-white hover:bg-red-100 border border-red-300 text-red-700 rounded text-[11px] font-medium"
              data-testid="repeat-offenders-clear-filters"
            >
              <X size={11} /> Clear filters
            </button>
          )}
          <span className="text-red-700">Action recommended</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-red-50/40 border-b">
            <tr>
              <SortHeader label="Staff" col="name" sortBy={sortBy} onSort={handleSort} align="left" />
              <SortHeader label="Geofence" col="geofence" sortBy={sortBy} onSort={handleSort} align="left" />
              <SortHeader label="Department" col="department" sortBy={sortBy} onSort={handleSort} align="left" />
              <SortHeader label="Times Late" col="total" sortBy={sortBy} onSort={handleSort} />
              <SortHeader label="% of Total" col="pct" sortBy={sortBy} onSort={handleSort} />
              <SortHeader label="Total Late Min" col="total_late_min" sortBy={sortBy} onSort={handleSort} />
              <SortHeader label="Worst (min)" col="worst_min" sortBy={sortBy} onSort={handleSort} />
            </tr>
            {/* Filter row — second header row. */}
            <tr className="bg-white border-b">
              <FilterTextCell value={filters.name} onChange={(v) => setFilters({ ...filters, name: v })} placeholder="Search name / ID…" testid="ro-filter-name" />
              <FilterTextCell value={filters.geofence} onChange={(v) => setFilters({ ...filters, geofence: v })} placeholder="Search geofence…" testid="ro-filter-geofence" />
              <FilterTextCell value={filters.department} onChange={(v) => setFilters({ ...filters, department: v })} placeholder="Search dept…" testid="ro-filter-department" />
              <FilterNumRangeCell minVal={filters.total_min} maxVal={filters.total_max} onMin={(v) => setFilters({ ...filters, total_min: v })} onMax={(v) => setFilters({ ...filters, total_max: v })} testid="ro-filter-total" />
              <FilterNumRangeCell minVal={filters.pct_min} maxVal={filters.pct_max} onMin={(v) => setFilters({ ...filters, pct_min: v })} onMax={(v) => setFilters({ ...filters, pct_max: v })} testid="ro-filter-pct" step="0.1" />
              <FilterNumRangeCell minVal={filters.total_late_min_min} maxVal={filters.total_late_min_max} onMin={(v) => setFilters({ ...filters, total_late_min_min: v })} onMax={(v) => setFilters({ ...filters, total_late_min_max: v })} testid="ro-filter-tlm" />
              <FilterNumRangeCell minVal={filters.worst_min_min} maxVal={filters.worst_min_max} onMin={(v) => setFilters({ ...filters, worst_min_min: v })} onMax={(v) => setFilters({ ...filters, worst_min_max: v })} testid="ro-filter-worst" />
            </tr>
          </thead>
          <tbody className="divide-y divide-red-100">
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-500 text-xs italic">
                  No rows match the current filters. <button onClick={clearFilters} className="text-red-700 underline hover:no-underline">Clear filters</button> to see all {offenders.length} offenders.
                </td>
              </tr>
            ) : sorted.map(s => (
              <tr key={s.sid} data-testid={`repeat-offender-row-${s.sid}`}>
                <td className="px-3 py-1.5 text-gray-900 font-semibold">
                  {s.name} <span className="text-gray-400 font-mono">{s.sid}</span>
                </td>
                <td className="px-3 py-1.5 text-gray-700 truncate max-w-[160px]" title={s.geofence}>{s.geofence}</td>
                <td className="px-3 py-1.5 text-gray-700 truncate max-w-[160px]" title={s.department}>{s.department}</td>
                <td className="px-3 py-1.5 text-center font-bold text-red-700 tabular-nums">{s.total}</td>
                <td className="px-3 py-1.5 text-center tabular-nums text-red-700">{s.pct.toFixed(1)}%</td>
                <td className="px-3 py-1.5 text-center tabular-nums text-gray-700">{s.total_late_min}</td>
                <td className="px-3 py-1.5 text-center tabular-nums text-gray-700">{s.worst_min}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-red-50/60 border-t-2 border-red-200">
            <tr data-testid="repeat-offenders-totals">
              <td colSpan={3} className="px-3 py-2 text-right text-[11px] font-bold text-red-900 uppercase">
                {hasAnyFilter ? 'Filtered subtotal' : 'Repeat offenders subtotal'}
              </td>
              <td className="px-3 py-2 text-center font-bold text-red-800 tabular-nums">{subTotalTimes}</td>
              <td className="px-3 py-2 text-center font-bold text-red-800 tabular-nums">{subTotalPct.toFixed(1)}%</td>
              <td className="px-3 py-2 text-center font-bold text-red-800 tabular-nums">{subTotalMin}</td>
              <td className="px-3 py-2 text-center text-red-700 text-[10px]">—</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function SortHeader({ label, col, sortBy, onSort, align = 'center' }) {
  const isActive = sortBy.key === col;
  const Icon = !isActive ? ArrowUpDown : (sortBy.dir === 'asc' ? ArrowUp : ArrowDown);
  return (
    <th
      onClick={() => onSort(col)}
      className={`px-3 py-2 text-${align} text-[10px] font-medium text-red-900 uppercase cursor-pointer select-none hover:bg-red-100/60`}
      data-testid={`ro-sort-${col}`}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'left' ? '' : 'justify-center'}`}>
        {label}
        <Icon size={10} className={isActive ? 'text-red-700' : 'text-red-300'} />
      </span>
    </th>
  );
}

function FilterTextCell({ value, onChange, placeholder, testid }) {
  return (
    <td className="px-2 py-1.5">
      <div className="relative">
        <Search size={10} className="absolute left-1.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-5 pr-1.5 py-1 text-[11px] border border-gray-200 rounded focus:outline-none focus:border-red-300"
          data-testid={testid}
        />
      </div>
    </td>
  );
}

function FilterNumRangeCell({ minVal, maxVal, onMin, onMax, testid, step = '1' }) {
  return (
    <td className="px-1 py-1.5">
      <div className="flex items-center gap-1">
        <input
          type="number"
          value={minVal}
          onChange={(e) => onMin(e.target.value)}
          placeholder="min"
          step={step}
          className="w-full px-1 py-1 text-[11px] text-center border border-gray-200 rounded focus:outline-none focus:border-red-300 tabular-nums"
          data-testid={`${testid}-min`}
        />
        <input
          type="number"
          value={maxVal}
          onChange={(e) => onMax(e.target.value)}
          placeholder="max"
          step={step}
          className="w-full px-1 py-1 text-[11px] text-center border border-gray-200 rounded focus:outline-none focus:border-red-300 tabular-nums"
          data-testid={`${testid}-max`}
        />
      </div>
    </td>
  );
}
